import { useCallback, useEffect, useState } from 'react';
import {
	BatchFilter,
	PagingConfig,
} from '../../../shared/VolunteerApplicationServiceTypes';
import { DEFAULT_PAGE_SIZE } from '../../../shared/Constants';
import { GetBatchesResponse } from '../../../shared/PreWorkServiceTypes';
import { useAuth } from '../../../hooks/useAuth';
import { useLoadingIndicator } from '../../../hooks/useLoadingIndicator';
import { useToastMessage } from '../../../hooks/useToastMessage';
import { getBatches } from '../../../service/PreWorkService';

export const useBatchList = () => {
	const { srcmId } = useAuth();
	const { setLoading } = useLoadingIndicator();
	const { showToastMessage } = useToastMessage();
	const [batchData, setBatchData] = useState<GetBatchesResponse>({
		batches: [],
		pagination: { totalNoOfRecords: 0 },
	});
	const [batchFilter, setBatchFilter] = useState<BatchFilter>({});
	const [batchPaging, setBatchPaging] = useState<PagingConfig>({});

	const onBatchViewFilterChange = useCallback((filterParam: BatchFilter) => {
		setBatchFilter(filterParam);
	}, []);

	const onBatchPageChange = useCallback(
		(pageNumber: number, pageSize: number) => {
			setBatchPaging({ pageNo: pageNumber, pageSize });
		},
		[]
	);

	const fetchBatchViewData = useCallback(
		async (pageNumber: number, pageSize: number, filter: BatchFilter) => {
			if (srcmId) {
				setLoading(true);
				const pageIndex = pageNumber < 1 ? 0 : pageNumber - 1;
				try {
					const batchDataWithoutKey = await getBatches({
						functionarySrcmId: srcmId,
						pageIndex,
						pageSize,
						batchFilter: filter,
					});
					const batchFetchedData = {
						...batchDataWithoutKey,
						...(batchDataWithoutKey.batches && {
							candidateBatches: batchDataWithoutKey.batches.map((batch) => ({
								...batch,
								key: batch.id,
							})),
						}),
					};

					if (
						batchFetchedData.pagination.totalNoOfPages &&
						batchFetchedData.pagination.totalNoOfPages > 0 &&
						pageNumber > batchFetchedData.pagination.totalNoOfPages
					) {
						onBatchPageChange(
							batchFetchedData.pagination.totalNoOfPages ?? 1,
							pageSize
						);
					} else {
						setBatchData(batchFetchedData);
					}
				} catch (e) {
					if (e instanceof Error) {
						showToastMessage('error', e.message);
					} else {
						showToastMessage('error', 'Unknown error occurred');
					}
				} finally {
					setLoading(false);
				}
			}
		},
		[srcmId, setLoading, showToastMessage, onBatchPageChange]
	);

	useEffect(() => {
		void fetchBatchViewData(
			batchPaging.pageNo ?? 1,
			batchPaging.pageSize ?? DEFAULT_PAGE_SIZE,
			batchFilter
		);
	}, [batchFilter, fetchBatchViewData, batchPaging]);

	return {
		batchPaging,
		onBatchPageChange,
		batchFilter,
		onBatchViewFilterChange,
		batchData,
	};
};
